import React, { type FC, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { useTranslation } from 'react-i18next'
import type { RootState } from '../../../redux/store'
import { CompanyIntegrationType } from '../../../api/admin-companies-api-types'
import {
  DeleteCompanyIntegrationAction,
  DownloadTransferFileForAdminAction,
  UpdateCompanyIntegrationAction
} from '../../../redux/admin/admin-companies-actions'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import { Collapse, FormControl, FormControlLabel, IconButton, InputLabel, type SelectChangeEvent } from '@mui/material'
import clsx from 'clsx'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Button from '@mui/material/Button'
import CardActions from '@mui/material/CardActions'
import { type AdminCompanyIntegration } from '../../../redux/admin/admin-companies-types'

interface CompanyData {
  id: string
  name: string
}

interface CompanyIntegrationCardProps {
  company: CompanyData
  classes: Record<string, string>
  isExpanded: boolean
  onExpandStateChange: (company: CompanyData, expand: boolean) => void
}

/**
 * Company integration details & edit form component.
 */
export const CompanyIntegrationCard: FC<CompanyIntegrationCardProps> = (props) => {
  const {
    company,
    classes,
    isExpanded,
    onExpandStateChange
  } = props

  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const integration = useAppSelector<AdminCompanyIntegration | undefined>((state: RootState) =>
    state.admin.companyIntegrations.find(it => it.companyId === company.id))

  const [integrationType, setIntegrationType] = React.useState<CompanyIntegrationType | ''>('')

  const [enabled, setEnabled] = React.useState<boolean>(integration?.enabled ?? false)
  const [cronString, setCronString] = React.useState<string>(integration?.cronString ?? '')
  const [url, setUrl] = React.useState<string>(integration?.url ?? '')
  const [username, setUsername] = React.useState<string>(integration?.username ?? '')
  const [password, setPassword] = React.useState<string>('')
  const [inPulseFormat, setInPulseFormat] = React.useState<string>(integration?.inPulseFormat ?? '')
  const [tietoIdentifier, setTietoIdentifier] = React.useState<string>(integration?.tietoIdentifier ?? '')

  const isDirty = (): boolean => {
    if (integration == null) {
      return true
    }

    return integrationType !== integration.integrationType ||
            enabled !== integration.enabled ||
            cronString !== integration.cronString ||
            url !== (integration.url ?? '') ||
            username !== (integration.username ?? '') ||
            password !== '' ||
            inPulseFormat !== (integration.inPulseFormat ?? '') ||
            tietoIdentifier !== (integration.tietoIdentifier ?? '')
  }

  const isValid = integrationType !== '' &&
        cronString !== '' &&
        url !== '' &&
        (username === '' || password !== '')

  const onSave = (): void => {
    if (!isDirty() || !isValid) {
      return
    }
    dispatch(UpdateCompanyIntegrationAction({
      companyId: company.id,
      integrationType,
      enabled,
      cronString,
      url,
      username: username !== '' ? username : undefined,
      inPulseFormat: integrationType === CompanyIntegrationType.InPulse && inPulseFormat !== ''
        ? inPulseFormat
        : undefined,
      tietoIdentifier: integrationType === CompanyIntegrationType.Aqua && tietoIdentifier !== ''
        ? tietoIdentifier
        : undefined,
      password: password !== '' ? password : undefined
    }))
  }

  const onDelete = (): void => {
    const msg: string = t('companyIntegrations.deleteConfirmation', { integrationType })
    if (window.confirm(msg)) {
      dispatch(DeleteCompanyIntegrationAction({ companyId: company.id }))
    }
  }

  const onGenerateFile = (): void => {
    dispatch(DownloadTransferFileForAdminAction({
      companyId: company.id
    }))
  }

  useEffect(() => {
    // sync values when company integration is loaded
    if (integration == null) {
      return
    }

    setIntegrationType(integration.integrationType)
    setEnabled(integration.enabled)
    setCronString(integration.cronString)
    setUrl(integration.url ?? '')
    setUsername(integration.username ?? '')
    setInPulseFormat(integration.inPulseFormat ?? '')
    setTietoIdentifier(integration.tietoIdentifier ?? '')
  }, [integration])

  return (
        <Card key={company.id} style={{ marginBottom: '10px' }}>
            <CardHeader
                title={company.name}
                subheader={integrationType ?? ''}
                action={
                    <IconButton
                        aria-expanded={isExpanded}
                        className={clsx(classes.expand, {
                          [classes.expandOpen]: isExpanded
                        })}
                        onClick={() => {
                          onExpandStateChange(company, !isExpanded)
                        }}
                        size="large"
                    >
                        <KeyboardArrowRight/>
                    </IconButton>
                }
            />
            <Collapse in={isExpanded} timeout="auto">
                <CardContent>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={(enabled ?? false)}
                                value="integrationEnabled"
                                onChange={(_event: React.ChangeEvent, checked: boolean) => {
                                  setEnabled(checked)
                                }}
                                color="primary"
                            />
                        }
                        label={t('settings_o.enabled')}
                    />
                    <FormControl fullWidth>
                        <InputLabel id='integration-type-label'>{t('companyIntegrations.integrationType')}</InputLabel>
                        <Select
                            labelId='integration-type-label'
                            label={t('companyIntegrations.integrationType')}
                            autoWidth={false}
                            value={integrationType}
                            required={true}
                            defaultValue={CompanyIntegrationType.Generic}
                            onChange={(event: SelectChangeEvent<CompanyIntegrationType>) => {
                              const isValidType = CompanyIntegrationType[event.target.value] != null
                              setIntegrationType(isValidType ? event.target.value as CompanyIntegrationType : '')
                            }}
                            variant="standard"
                        >
                            <MenuItem key='nothing' value="">{t('nothing')}</MenuItem>
                            {Object.entries(CompanyIntegrationType).map(([key, _value]) => (
                                <MenuItem key={key} value={key}>{key}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {integrationType === CompanyIntegrationType.InPulse &&
                      <List>
                        <ListItem key={`in_pulse_format_${company.id}`}>
                          <TextField
                            key={`${company.id}inpulse`}
                            id={`${company.id}inpulse`}
                            label="IN_PULSE Format"
                            defaultValue={inPulseFormat ?? ''}
                            fullWidth
                            onBlur={(event) => {
                              setInPulseFormat(event.target.value)
                            }}
                            variant="standard"
                          />
                        </ListItem>
                      </List>
                    }
                    {integrationType === CompanyIntegrationType.Aqua &&
                      <List>
                        <ListItem key={`aqua_${company.id}`}>
                          <TextField
                            id={`${company.id}tietoIdentifier`}
                            label="Tieto Identifier"
                            type="text"
                            defaultValue={tietoIdentifier}
                            fullWidth
                            onBlur={(event) => {
                              setTietoIdentifier(event.target.value)
                            }}
                            variant="standard"
                          />
                        </ListItem>
                      </List>
                    }
                    <TextField
                        id={company.id}
                        label="Ajastus (Quartz Cron)"
                        required={true}
                        defaultValue={cronString ?? ''}
                        fullWidth
                        placeholder='0 1 * * * * *'
                        error={cronString !== '' && !isValidCronSchedule(cronString)}
                        className={classes.textField}
                        onChange={(event) => {
                          setCronString(event.target.value)
                        }}
                        variant="standard"
                    />
                    <TextField
                        id={`${company.id}url`}
                        label="URL"
                        required={true}
                        defaultValue={url}
                        fullWidth
                        onBlur={(event) => {
                          setUrl(event.target.value)
                        }}
                        variant="standard"
                    />
                    <TextField
                        id={`${company.id}username`}
                        label={t('username')}
                        defaultValue={username}
                        fullWidth
                        onChange={(event) => {
                          setUsername(event.target.value)
                        }}
                        variant="standard"
                    />
                    <TextField
                        id={`${company.id}password`}
                        label={t('password')}
                        type="text"
                        defaultValue=""
                        placeholder={'*************'}
                        required={username !== ''}
                        error={username !== '' && password === ''}
                        fullWidth
                        onChange={(event) => {
                          setPassword(event.target.value)
                        }}
                        variant="standard"
                    />
                </CardContent>
                <CardActions>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!(isValid && isDirty())}
                        onClick={onSave}
                    >
                        {t('common.actions.save')}
                    </Button>
                </CardActions>
                <CardActions>
                    <Button
                        variant="contained"
                        color="inherit"
                        disabled={integration == null}
                        onClick={onDelete}
                    >
                        {t('companyIntegrations.deleteButton')}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={integration == null}
                        onClick={onGenerateFile}
                    >
                        {t('companyIntegrations.generateTransferFile')}
                    </Button>
                </CardActions>
            </Collapse>
        </Card>
  )
}

/* Helper Functions */

function isValidCronSchedule(value: string): boolean {
  // According to https://www.quartz-scheduler.org/documentation/quartz-2.3.0/tutorials/crontrigger.html
  const expectedNumOfFields = 7
  return value.split(' ').length === expectedNumOfFields
}
